import { generatePath, matchPath, parsePath } from 'react-router'

import type { CurrencyAddress } from '@root/entities/currency/model/CurrencyAddress'
import type { Network } from '@root/entities/network'
import type { PairAddress } from '@root/entities/pair'
import type { DelugeId } from '@root/entities/user-short'
import type { WalletId } from '@root/entities/wallet/model'

// TODO: Make high order class with static methods and instance with resolved params

class Route<Params extends Record<string, string> | undefined = undefined> {
  public path: string
  constructor({ path }: { path: string }) {
    this.path = path
  }

  public build: keyof Params extends never
    ? () => string
    : (params: Params) => string = (params?: Params) => {
    return generatePath(this.path, params)
  }

  public match = (path: string) => {
    const pathObj = parsePath(path)
    return matchPath(this.path, pathObj.pathname ?? '') ?? undefined
  }
}

export const PremiumRoute = new Route({ path: '/premium' })
export const PremiumCurrencyRoute = new Route<{
  currency: CurrencyAddress
}>({
  path: '/premium/currency/:currency',
})
export const FunExploreRoute = new Route({ path: '/fun-explore' })

export const TradeRoute = new Route<{
  network: Network.Network
  pairAddress: PairAddress
}>({
  path: '/trade/:network/:pairAddress',
})

export const NftRoute = new Route({ path: '/nft' })

export const ProfileRoute = new Route<{ delugeId?: DelugeId }>({
  path: '/profile/:delugeId?',
})

export const ProfileReferralLeaderboardRoute = new Route({
  path: '/profile/referral-leaderboard',
})

export const ProfileTradingLeaderboardRoute = new Route({
  path: '/profile/trading-leaderboard',
})

export const ProfileReferralUsersRoute = new Route({
  path: '/profile/referral-users',
})

export const AuthRoute = new Route({ path: '/auth' })
export const RootRoute = new Route({ path: '/' })
/**
 * The redesigned pulse board, mounted beside the old one rather than over it,
 * so the two can be opened side by side and compared on the same feed.
 */
export const PulseV2Route = new Route({ path: '/pulse-v2' })
/**
 * The account screen in the same language, mounted beside the wallet manager
 * rather than over it: the manager is where a wallet is created, exported and
 * emptied, and this is where the money is read.
 */
export const PortfolioV2Route = new Route({ path: '/portfolio-v2' })
/**
 * One token, in the same language. Beside the old trade page rather than over
 * it, so the two can be opened on the same token and compared.
 */
export const TradeV2Route = new Route<{
  network: string
  pairAddress: string
}>({
  path: '/trade-v2/:network/:pairAddress',
})
export const OrdersRoute = new Route({ path: '/orders' })
/**
 * Not mounted anywhere.
 *
 * The analyser was showing a loading error over an empty week and calling it
 * a portfolio; it is off the navigation and off the router until it has
 * something true to say. The path is kept so nothing has to guess it back.
 */
export const PortfolioRoute = new Route({ path: '/portfolio' })

export const WalletManagerRoute = new Route({ path: '/wallet-manager' })
export const WalletTrackerRoute = new Route({ path: '/wallet-tracker' })

// #region Mobile only routes
export const WalletManagerWalletRoute = new Route<{
  walletId: WalletId
}>({
  path: '/wallet-manager/:walletId',
})
export const WalletSwapRoute = new Route<{
  walletId: WalletId
}>({
  path: '/wallet-manager/:walletId/swap',
})
export const WalletTransferRoute = new Route<{
  walletId: WalletId
}>({
  path: '/wallet-manager/:walletId/transfer',
})
export const NewWalletRoute = new Route({ path: '/wallet-manager/new-wallet' })
export const WatchlistRoute = new Route({ path: '/watchlist' })

// #endregion Mobile only routes
